import { HttpClient } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import { SharedService } from "./shared.service";
import { AlertController } from "@ionic/angular";
import { BehaviorSubject, firstValueFrom, from, interval, map, Observable, of } from "rxjs";
import { jwtDecode } from "jwt-decode";
import { Universe } from "../interfaces/universe.interface";
import { TranslateService } from "@ngx-translate/core";
import { Auth, signInWithEmailAndPassword } from '@angular/fire/auth';
import { environment } from "src/environments/environment";

@Injectable({
	providedIn: 'root'
})
export class AuthService {

	private sharedService = inject(SharedService);
	private http = inject(HttpClient);
	private translate = inject(TranslateService);
	private router = inject(Router);
	private alertCtrl = inject(AlertController);
	private firebaseAuth: Auth = inject(Auth);

	// public should_keep_alive:boolean = false;
	public currentUniverse?: Universe;
	private currentUniverseSubject = new BehaviorSubject<Universe | null>(null);
	public currentUniverse$ = this.currentUniverseSubject.asObservable();

	public PRIVILEGE_USER = 1;
	public PRIVILEGE_MKT = 2;
	public PRIVILEGE_ADMIN = 8;
	public PRIVILEGE_SUPERADMIN = 9;

	public PLATFORM_ACCESS_LEVEL = 0;

	is_logged_in = signal<boolean>(!!localStorage.getItem('token'));
	is_alert_open:boolean = false;

	is_logged_in_firebase:boolean = false;

	private KEEP_ALIVE_INTERVAL = 2 * 60 * 1000; // 2 minutos * 60 segundos

	constructor(
	) {
		interval(this.KEEP_ALIVE_INTERVAL).subscribe(() => {
			this.keepAlive();
		});
	}

	async ngOnInit() {
		this.PLATFORM_ACCESS_LEVEL = await this.getPlatformRole();
	}

	async keepAlive() {
		if (this.is_logged_in()) {
			this.http.post<any>(`${this.sharedService.urls.auth.refresh}`, {
				token: localStorage.getItem('token')
			}).subscribe({
				next: (response) => {
					localStorage.setItem('token', response.token);
				}, error: async (error) => {
					console.error('keepAlive failed', error);

					if (this.is_alert_open) return;

					this.logout(true);

					this.is_alert_open = true;
					const alert = await this.alertCtrl.create({
						header: 'Error',
						message: error.message,
						buttons: [{
							text: 'OK',
							handler: () => {
								this.is_alert_open = false;
							}
						}]
					});
					await alert.present();

				}
			});
		}
	}

	async onLoginSuccess() {
		this.PLATFORM_ACCESS_LEVEL = await this.getPlatformRole();
		await this.router.navigate(['/'], { replaceUrl: true });
		// this.should_keep_alive = true;
		this.is_logged_in.set(true);
	}

	async logout(redirect: boolean = false) {
		this.PLATFORM_ACCESS_LEVEL = 0;
		localStorage.removeItem('token');

		this.is_logged_in.set(false);
		if (redirect) {
			await this.router.navigate(['/login'], { replaceUrl: true });
		}
		// this.should_keep_alive = false;
	}

	async login(user: string, password: string) {

		const data =  {
			email: user,
			password: password
		};
		try {
			const response = await firstValueFrom(this.http.post<any>(`${this.sharedService.urls.auth.login}`, data));

			if (response.token) {
				localStorage.setItem('token', response.token);
				this.onLoginSuccess();
			} else {
				const message:string = this.translate.instant("response.error.login");
				const alert = await this.alertCtrl.create({
					header: 'Error',
					message: message,
					buttons: ['OK']
				});
				await alert.present();
			}
		} catch (error:any) {
			console.error('Login failed', error);

			const status = error.error?.status || error.status || this.translate.instant("response.error.unknown_status");
			const statusText = error.error?.statusText || error.statusText || this.translate.instant("response.error.unknown_status_text");

			const message:string = `${this.translate.instant("response.error.login")}: ${status} - ${statusText}`;
			const alert = await this.alertCtrl.create({
				header: 'Error',
				message: message,
				buttons: ['OK']
			});
			await alert.present();
		}	

	}

	async recover(email: string) {
		let msg = '';
		let isOk = false;
		try {
			const data =  { email: email };
			const response = await firstValueFrom(this.http.post<any>(`${this.sharedService.urls.auth.recover}`, data));

			// console.log(response);
			if (response.status==1) {
				isOk = true;
				msg = response.msg;
			} else {
				msg = response.msg;
			}
		} catch (error:any) {
			console.error('recover failed', error);
			msg = error.message || error.toString();
		}

		const alert = await this.alertCtrl.create({
			header: this.translate.instant(isOk ? 'response.success.title' : 'response.error.title'),
			message: msg,
			buttons: ['OK']
		});
		await alert.present();
	}

	async reset(token: string, psw: string) {
		let msg = '';
		let isOk = false;
		try {
			const data =  { token: token, password: psw };
			const response = await firstValueFrom(this.http.post<any>(`${this.sharedService.urls.auth.reset}`, data));

			// console.log(response);
			if (response.status==1) {
				isOk = true;
				msg = response.msg;
			} else {
				msg = response.msg;
			}
		} catch (error:any) {
			console.error('recover failed', error);
			msg = error.message || error.toString();
		}

		const alert = await this.alertCtrl.create({
			header: this.translate.instant(isOk ? 'response.success.title' : 'response.error.title'),
			message: msg,
			buttons: [{
				text: 'OK',
				handler: () => {
					if (isOk) {
						this.router.navigate(['/login'], { replaceUrl: true });
					}
				}
			}]
		});
		await alert.present();
	}


	async getRole(): Promise<number> {
		// si no tiene universo activo actualmente
		if (!this.currentUniverse) {
			const universes = await this.sharedService.getUniverses();
			if (universes.length) {
				let universe = universes.find((u:Universe) => u.is_default);
				if (!universe) {
					universe = universes[0];
				}
				this.currentUniverse = universe;
			}
		}
		if (!this.currentUniverse) return 0;

		return this.currentUniverse.id_role;
	}

	getRole$(): Observable<number> {

		// Si ya existe, devolver directamente
		if (this.currentUniverse) {
			return of(this.currentUniverse.id_role);
		}

		// Convertimos la Promise a Observable
		return from(this.sharedService.getUniverses()).pipe(
			map((universes: Universe[]) => {

				if (!universes?.length) { return 0; }

				let universe = universes.find(u => u.is_default);

				if (!universe) {
					universe = universes[0];
				}

				this.currentUniverse = universe;

				return universe?.id_role ?? 0;
			})
		);
	}

	async getPlatformRole(): Promise<number> {
		const token = localStorage.getItem('token');
		if (!token) return 0;

		const decoded: any = jwtDecode(token);
		const roles = decoded.roles;
		let role = this.PRIVILEGE_USER; // ROLE_USER
		if (roles.length > 0) {
			switch (roles[0]) {
				case 'ROLE_MARKETING':
					role = this.PRIVILEGE_MKT;
					break;
				case 'ROLE_ADMIN':
					role = this.PRIVILEGE_ADMIN;
					break;
				case 'ROLE_SUPERADMIN':
					role = this.PRIVILEGE_SUPERADMIN;
					break;
				default:
					break;
			}
		}

		return role;
	}


	async signInFirebase() {
		if (this.is_logged_in_firebase) return;
		try {
			const userCredential = await signInWithEmailAndPassword(
				this.firebaseAuth,
				environment.firebaseServiceUser.email,
				environment.firebaseServiceUser.password
			);

			// console.log('Usuario autenticado:', userCredential.user.uid);
			this.is_logged_in_firebase = true;
		} catch (error) {
			console.error('Error autenticando:', error);
		}
  }
}